import * as React from 'react';
import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { DefaultDatePicker } from '../../../../global/default-datepicker/default-datepicker';
import { SelectTimezones } from '../../../../global/select-timezone/select-timezones';
import { defaultTextAreaValues } from '../../../../global/text-area/text-area';
import './meeting-form-fields.scss';
import { FormField } from '../../../../global/form/form-field/form-field';
import { SelectGroup } from '../../../../global/select-group/select-group';
import { MeetingFieldsViewModel } from './meeting-form-fields.viewmodel';
import { TOPIC_TEXT } from '../../../../../constants/common-labels.constants';

const defaultProps = {
    strictParsing: true,
    showTimeSelect: true,
    isClearable: true,
    timeIntervals: 15,
};

const MeetingFormFields = injectIntl(mobxReact.observer((props: { vm: MeetingFieldsViewModel; clonedMeetingId?; intl: IntlShape }) => {
    const { vm, clonedMeetingId, intl } = props;

    return (
        <div className='oe-form__body'>

            {/* GROUP */}
            {!vm.isEditMode && <FormField label={
                <FormattedMessage
                    id='oe.common.group'
                    defaultMessage='Group'
                    description='Label text'
                />
            }>
                <SelectGroup
                    onChange={mobx.action(selectedGroupId => vm.selectedGroupId = selectedGroupId)}
                    value={vm.getGroup()}
                    className='field w-100'
                />
                {vm.showErrorMessages() && <span className='oe-form-field__error'>{vm.getGroupError()}</span>}
            </FormField>}

            {/* START TIME */}
                <FormField label={
                    <FormattedMessage
                        id='oe.common.startDateAndTime'
                        defaultMessage='Start Date and Time'
                        description='Label text'
                    />}
                >
                <DefaultDatePicker
                    selected={vm.getStartDateTime()}
                    onChange={date => vm.setStartDateTime(date)}
                    placeholderText={
                        intl.formatMessage({
                            id: `oe.common.selectStartDateTime`,
                            defaultMessage: 'Select start date time',
                            description: 'Label text',
                        })
                    }
                    minDate={vm.getMinStartDateTime()}
                    maxDate={vm.getMaxStartDateTime()}
                    className='datpicker-input field w-100'
                    {...defaultProps}
                />
                {vm.showErrorMessages() && <span className='oe-form-field__error'>{vm.getStartTimeError()}</span>}
                {vm.showErrorMessages() && vm.isNeedShowStartBeforeError() &&
                <span className='oe-form-field__error'>{vm.getTimeBeforeError()}</span>}
            </FormField>

            {/* END TIME */}
            <FormField label={
                <FormattedMessage
                    id='oe.common.endDateAndTime'
                    defaultMessage='End Date and Time'
                    description='Label text'
                />}
            >
                <DefaultDatePicker
                    selected={vm.getEndDateTime()}
                    onChange={date => vm.setEndDateTime(date)}
                    placeholderText={
                        intl.formatMessage({
                            id: `oe.common.selectEndDateTime`,
                            defaultMessage: 'Select end date time',
                            description: 'Label text',
                        })
                    }
                    minDate={vm.getMinEndTime()}
                    maxDate={vm.getMaxEndDateTime()}
                    className='datpicker-input field w-100'
                    {...defaultProps}
                />
                {vm.showErrorMessages() && <span className='oe-form-field__error'>{vm.getEndTimeError()}</span>}
                {vm.showErrorMessages() && vm.isNeedShowEndBeforeError() &&
                <span className='oe-form-field__error'>{vm.getTimeBeforeError()}</span>}
            </FormField>

            {/* TIME ZONE */}
            <FormField label={
                <FormattedMessage
                    id='oe.common.timeZone'
                    defaultMessage='Time Zone'
                    description='Label text'
                />}
            >
                <SelectTimezones
                    onChange={mobx.action(timeZone => vm.timeZone = timeZone)}
                    value={vm.getTimeZone()}
                    className='field w-100'
                    placeHolder={!clonedMeetingId ?
                        intl.formatMessage({
                            id: `oe.common.selectTimeZone`,
                            defaultMessage: 'Select time zone',
                            description: 'Label text',
                        }) : ''}
                />
                {vm.showErrorMessages() && <span className='oe-form-field__error'>{vm.getTimeZoneError()}</span>}
            </FormField>

            {/* TOPIC */}
            <FormField label={<FormattedMessage {...TOPIC_TEXT} />}
            >
                <input
                    type='text'
                    className='field w-100'
                    value={vm.topic}
                    onChange={mobx.action(e => vm.topic = e.target.value)}
                />
                {vm.showErrorMessages() && <span className='oe-form-field__error'>{vm.getTopicError()}</span>}
                {vm.getTopicLengthError() && <span className='oe-form-field__error'>{vm.getTopicLengthError()}</span>}
            </FormField>

            {/* DESCRIPTION */}
            <FormField label={
                <FormattedMessage
                    id='oe.common.descriptionOptional'
                    defaultMessage='Description (Optional)'
                    description='Label text'
                />}
            >
                <textarea
                    onChange={mobx.action(e => vm.description = e.target.value)}
                    className='description-field'
                    value={vm.description}
                    {...defaultTextAreaValues}
                />
                {vm.getDescriptionLengthError() &&
                <span className='oe-form-field__error'>{vm.getDescriptionLengthError()}</span>}
            </FormField>
        </div>
    );
}));

export { MeetingFormFields };
